import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as AntTooltip, Button, Checkbox } from 'antd';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { UnorderedListOutlined, PieChartOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { PERIOD1_COLOR, PERIOD2_COLOR, PERCENT_THRESHOLD, MAX_SLICES } from '../constants/comparisonConstants';
import { isValidArray } from '../utils/comparisonUtils';
import PeriodDot from '../components/PeriodDot';
import { legendItemStyle, legendSeparatorStyle } from '../styles/comparisonStyles';

// 100 cores fixas (hex) - para diferenciação entre categorias
const fixedColors = [
    '#1B3A7C', '#4A90E2', '#F5A623', '#D622A9', '#BD10E0', '#9013FE', '#50E3C2', '#7ED321', '#B8E986', '#F8E71C',
    '#F45B69', '#417505', '#FF7F50', '#7F8C8D', '#3E4A59', '#FF5733', '#C70039', '#900C3F', '#581845', '#FFC300',
    '#DAF7A6', '#1ABC9C', '#2ECC71', '#3498DB', '#9B59B6', '#34495E', '#16A085', '#27AE60', '#2980B9', '#8E44AD',
    '#2C3E50', '#F1C40F', '#E67E22', '#E74C3C', '#ECF0F1', '#95A5A6', '#F39C12', '#D35400', '#C0392B', '#BDC3C7',
    '#FF9999', '#FF6666', '#FF3333', '#FF0000', '#CC0000', '#990000', '#660000', '#330000', '#FFCC99', '#FF9966',
    '#FF6633', '#CC3300', '#993300', '#663300', '#FFCC00', '#FF9900', '#FF6600', '#CC6600', '#FFFF99', '#FFFF66',
    '#FFFF33', '#FFFF00', '#CCCC00', '#999900', '#666600', '#333300', '#99FF99', '#66FF66', '#33FF33', '#00FF00',
    '#00CC00', '#009900', '#006600', '#003300', '#99FFFF', '#66FFFF', '#33FFFF', '#00FFFF', '#00CCCC', '#009999',
    '#006666', '#003333', '#9999FF', '#6666FF', '#3333FF', '#0000FF', '#0000CC', '#000099', '#000066', '#000033',
    '#E6B0AA', '#D98880', '#C39BD3', '#7FB3D5', '#76D7C4', '#7DCEA0', '#F7DC6F', '#F0B27A', '#E59866', '#B22222'
];

const DonutChartComparison = memo(({ data = [], data2 = [], categories = [], categories2 = [], onClick, activeFilters = [], disabled = false, columnsCount = 3, onRenderHeaderButton, renderMode = 'both', onOthersClick }) => {
    const { t } = useTranslation();
    const isFourColumns = columnsCount >= 4;

    // Combina dados de ambos os períodos
    const combinedData = useMemo(() => {
        const categoryMap = new Map();

        // Adiciona dados do período 1
        data.forEach((value, index) => {
            const category = categories[index];
            if (category) {
                categoryMap.set(category, {
                    name: category,
                    valuePeriod1: value || 0,
                    valuePeriod2: 0
                });
            }
        });

        // Adiciona dados do período 2
        data2.forEach((value, index) => {
            const category = categories2[index];
            if (category) {
                const existing = categoryMap.get(category);
                if (existing) {
                    existing.valuePeriod2 = value || 0;
                } else {
                    categoryMap.set(category, {
                        name: category,
                        valuePeriod1: 0,
                        valuePeriod2: value || 0
                    });
                }
            }
        });

        return Array.from(categoryMap.values());
    }, [data, data2, categories, categories2]);

    // Calcula totais
    const totalPeriod1 = useMemo(() => combinedData.reduce((sum, item) => sum + item.valuePeriod1, 0), [combinedData]);
    const totalPeriod2 = useMemo(() => combinedData.reduce((sum, item) => sum + item.valuePeriod2, 0), [combinedData]);
    const totalCombined = totalPeriod1 + totalPeriod2;

    // Prepara dados originais com percentuais
    const originalData = useMemo(() => {
        return combinedData.map(item => ({
            ...item,
            totalValue: item.valuePeriod1 + item.valuePeriod2,
            percentPeriod1: totalPeriod1 > 0 ? (item.valuePeriod1 / totalPeriod1) * 100 : 0,
            percentPeriod2: totalPeriod2 > 0 ? (item.valuePeriod2 / totalPeriod2) * 100 : 0,
            percentCombined: totalCombined > 0 ? ((item.valuePeriod1 + item.valuePeriod2) / totalCombined) * 100 : 0
        }));
    }, [combinedData, totalPeriod1, totalPeriod2, totalCombined]);

    // Ordena por valor total combinado
    const sortedListData = useMemo(() => {
        return [...originalData].sort((a, b) => b.totalValue - a.totalValue);
    }, [originalData]);

    // Validação de props
    if (!isValidArray(data) && !isValidArray(data2)) {
        return null;
    }

    // Lógica de agrupamento OTHERS (usa constantes importadas)
    // Aplica agrupamento apenas quando houver mais de 9 itens;
    // caso contrário, todas as categorias aparecem individualmente.
    const totalItems = sortedListData.length;

    let smallSlices = [];
    let bigSlices = [];
    let visibleBig = [];
    let overflowBig = [];
    let othersGroup = [];
    let othersValuePeriod1 = 0;
    let othersValuePeriod2 = 0;
    let othersTotal = 0;
    let othersPercent = 0;

    if (totalItems > 9) {
        smallSlices = sortedListData.filter(i => i.percentCombined < PERCENT_THRESHOLD);
        bigSlices = sortedListData.filter(i => i.percentCombined >= PERCENT_THRESHOLD);
        visibleBig = bigSlices.slice(0, MAX_SLICES - 1);
        overflowBig = bigSlices.slice(MAX_SLICES - 1);

        othersGroup = [...smallSlices, ...overflowBig];
        othersValuePeriod1 = othersGroup.reduce((sum, i) => sum + i.valuePeriod1, 0);
        othersValuePeriod2 = othersGroup.reduce((sum, i) => sum + i.valuePeriod2, 0);
        othersTotal = othersValuePeriod1 + othersValuePeriod2;
        othersPercent = totalCombined > 0 ? (othersTotal / totalCombined) * 100 : 0;
    } else {
        bigSlices = [...sortedListData];
        visibleBig = [...sortedListData];
        overflowBig = [];
        othersGroup = [];
        othersValuePeriod1 = 0;
        othersValuePeriod2 = 0;
        othersTotal = 0;
        othersPercent = 0;
    }

    // Monta dados do gráfico para Período 1
    const chartDataPeriod1 = useMemo(() => {
        const result = [];
        
        // Adiciona categorias visíveis do período 1
        visibleBig.forEach(item => {
            if (item.valuePeriod1 > 0) {
                result.push({
                    name: item.name,
                    categoryName: item.name,
                    value: item.valuePeriod1,
                    period: 1,
                    percent: totalPeriod1 > 0 ? (item.valuePeriod1 / totalPeriod1) * 100 : 0,
                    displayName: item.name
                });
            }
        });

        // Adiciona OTHERS se houver
        if (othersGroup.length > 0 && othersValuePeriod1 > 0) {
            result.push({
                name: "OTHERS",
                categoryName: "OTHERS",
                value: othersValuePeriod1,
                period: 1,
                percent: totalPeriod1 > 0 ? (othersValuePeriod1 / totalPeriod1) * 100 : 0,
                displayName: t("Dashboard.Others")
            });
        }

        return result;
    }, [visibleBig, othersGroup, othersValuePeriod1, totalPeriod1, t]);

    // Monta dados do gráfico para Período 2
    const chartDataPeriod2 = useMemo(() => {
        const result = [];
        
        // Adiciona categorias visíveis do período 2
        visibleBig.forEach(item => {
            if (item.valuePeriod2 > 0) {
                result.push({
                    name: item.name,
                    categoryName: item.name,
                    value: item.valuePeriod2,
                    period: 2,
                    percent: totalPeriod2 > 0 ? (item.valuePeriod2 / totalPeriod2) * 100 : 0,
                    displayName: item.name
                });
            }
        });

        // Adiciona OTHERS se houver
        if (othersGroup.length > 0 && othersValuePeriod2 > 0) {
            result.push({
                name: "OTHERS",
                categoryName: "OTHERS",
                value: othersValuePeriod2,
                period: 2,
                percent: totalPeriod2 > 0 ? (othersValuePeriod2 / totalPeriod2) * 100 : 0,
                displayName: t("Dashboard.Others")
            });
        }

        return result;
    }, [visibleBig, othersGroup, othersValuePeriod2, totalPeriod2, t]);

    // Mapeamento de cores por categoria
    const categoryColors = useMemo(() => {
        const map = {};
        const uniqueCategories = [...new Set(visibleBig.map(item => item.name))];
        uniqueCategories.forEach((category, index) => {
            map[category] = fixedColors[index % fixedColors.length];
        });
        map["OTHERS"] = '#CCCCCC';
        return map;
    }, [visibleBig]);

    // Estados
    const [activeCategories, setActiveCategories] = useState([]);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [showOthersItems, setShowOthersItems] = useState(false);
    const [showDetails, setShowDetails] = useState(!isFourColumns);

    useEffect(() => {
        if (JSON.stringify(activeFilters) !== JSON.stringify(activeCategories)) {
            setActiveCategories(activeFilters);
        }
    }, [activeFilters]);

    useEffect(() => {
        setShowDetails(!isFourColumns);
    }, [isFourColumns]);

    useEffect(() => {
        if (onRenderHeaderButton && isFourColumns) {
            onRenderHeaderButton(
                <Button
                    type="text"
                    icon={showDetails ? <PieChartOutlined /> : <UnorderedListOutlined />}
                    onClick={() => setShowDetails(!showDetails)}
                    style={{
                        color: showDetails ? '#4085c6' : '#8c8c8c',
                        padding: '4px 8px',
                        height: 'auto',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#4085c6';
                        e.currentTarget.style.backgroundColor = '#f0f7ff';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = showDetails ? '#4085c6' : '#8c8c8c';
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={showDetails ? t('Dashboard.ShowChart') : t('Dashboard.Details')}
                />
            );
        } else if (onRenderHeaderButton) {
            onRenderHeaderButton(null);
        }
    }, [showDetails, isFourColumns, onRenderHeaderButton, t]);

    const toggleSelection = (category) => {
        if (disabled) return;
        if (category === "OTHERS") return;
        setActiveCategories((prev) => {
            const isActive = prev.includes(category);
            const newCategories = isActive ? prev.filter(c => c !== category) : [...prev, category];
            onClick && onClick(category);
            return newCategories;
        });
    };

    const onPieClickPeriod1 = (_, index) => {
        if (index >= 0 && index < chartDataPeriod1.length) {
            const clickedItem = chartDataPeriod1[index];
            if (clickedItem && clickedItem.categoryName === "OTHERS") {
                setShowOthersItems(!showOthersItems);
                onOthersClick && onOthersClick();
            } else if (clickedItem && clickedItem.categoryName !== "OTHERS") {
                toggleSelection(clickedItem.categoryName);
            }
        }
    };

    const onPieClickPeriod2 = (_, index) => {
        if (index >= 0 && index < chartDataPeriod2.length) {
            const clickedItem = chartDataPeriod2[index];
            if (clickedItem && clickedItem.categoryName === "OTHERS") {
                setShowOthersItems(!showOthersItems);
                onOthersClick && onOthersClick();
            } else if (clickedItem && clickedItem.categoryName !== "OTHERS") {
                toggleSelection(clickedItem.categoryName);
            }
        }
    };

    const onListItemMouseEnter = (category) => {
        setHoveredCategory(category);
    };

    const onListItemMouseLeave = () => {
        setHoveredCategory(null);
    };

    const onListItemClick = (category, e) => {
        e.stopPropagation();
        if (category === "OTHERS") {
            setShowOthersItems(!showOthersItems);
            onOthersClick && onOthersClick();
        } else {
            toggleSelection(category);
        }
    };

    const getColor = (categoryName, period) => {
        if (categoryName === "OTHERS") return '#CCCCCC';
        return categoryColors[categoryName] || PERIOD1_COLOR;
    };

    if ((!data.length && !data2.length) || (!categories.length && !categories2.length)) {
        return <p>{t('Dashboard.Not')}</p>;
    }

    const shouldShowLabels = true;
    const shouldShowCharts = renderMode === 'charts' || renderMode === 'both';
    const shouldShowDetails = renderMode === 'details' || renderMode === 'both';
    const chartsContainerWidth = renderMode === 'charts' ? "100vw" : (isFourColumns ? (showDetails ? "0%" : "100%") : "100vw");
    const detailsWidth = renderMode === 'details' ? "100%" : (isFourColumns ? (showDetails ? "100%" : "0%") : "30%");
    const individualChartWidth = "50%";
    const chartHeight = 250;
    const innerRadius = 55;
    const outerRadius = 85;

    // Lista de dados para exibição (agrupados por categoria)
    const listData = useMemo(() => {
        const result = [];
        visibleBig.forEach(item => {
            result.push({
                name: item.name,
                displayName: item.name,
                valuePeriod1: item.valuePeriod1,
                valuePeriod2: item.valuePeriod2,
                totalValue: item.totalValue,
                percentPeriod1: item.percentPeriod1,
                percentPeriod2: item.percentPeriod2,
                percentCombined: item.percentCombined
            });
        });
        if (othersGroup.length > 0) {
            result.push({
                name: "OTHERS",
                displayName: t("Dashboard.Others"),
                valuePeriod1: othersValuePeriod1,
                valuePeriod2: othersValuePeriod2,
                totalValue: othersTotal,
                percentPeriod1: totalPeriod1 > 0 ? (othersValuePeriod1 / totalPeriod1) * 100 : 0,
                percentPeriod2: totalPeriod2 > 0 ? (othersValuePeriod2 / totalPeriod2) * 100 : 0,
                percentCombined: othersPercent
            });
        }
        return result;
    }, [visibleBig, othersGroup, othersValuePeriod1, othersValuePeriod2, othersTotal, othersPercent, totalPeriod1, totalPeriod2, t]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                flexWrap: 'nowrap',
                gap: 12,
                position: 'relative',
                minHeight: chartHeight,
                width: '100%'
            }}
        >
            {shouldShowCharts && renderMode !== 'details' && (!isFourColumns || !showDetails) && (
                <div style={{
                    width: chartsContainerWidth,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8,
                    transition: 'width 0.3s ease, opacity 0.3s ease',
                    opacity: isFourColumns && showDetails ? 0 : 1
                }}>
                    {/* Gráfico Período 1 */}
                    <ResponsiveContainer 
                        width={individualChartWidth} 
                        height={chartHeight}
                    >
                        <PieChart>
                            <Pie
                                data={chartDataPeriod1}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                minAngle={5}
                                paddingAngle={2}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                                onClick={onPieClickPeriod1}
                                onMouseEnter={(data, index, e) => {
                                    if (data && data.payload) {
                                        const category = data.payload.categoryName;
                                        if (category !== hoveredCategory) {
                                            onListItemMouseEnter(category);
                                        }
                                    }
                                }}
                                onMouseLeave={onListItemMouseLeave}
                                cursor={disabled ? "not-allowed" : "pointer"}
                                label={shouldShowLabels ? ({ payload, cx, cy, midAngle, outerRadius, index }) => {
                                    if (!payload) return null;
                                    const { value, percent } = payload;
                                    const RADIAN = Math.PI / 180;
                                    const radius = outerRadius + 30;
                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                    const percentValue = Number(percent) || 0;
                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            fill="#262626"
                                            textAnchor={x > cx ? "start" : "end"}
                                            dominantBaseline="central"
                                            fontSize="11px"
                                            fontWeight="600"
                                        >
                                            <tspan>{value} ({percentValue.toFixed(2)}%)</tspan>
                                        </text>
                                    );
                                } : false}
                            >
                                {chartDataPeriod1.map((entry, index) => {
                                    const opacity = hoveredCategory
                                        ? (entry.categoryName === hoveredCategory ? 1 : 0.3)
                                        : (activeCategories.length === 0 || activeCategories.includes(entry.categoryName) ? 1 : 0.3);
                                    const isHovered = hoveredCategory === entry.categoryName;
                                    const isActive = activeCategories.length === 0 || activeCategories.includes(entry.categoryName);
                                    const color = getColor(entry.categoryName, entry.period);
                                    
                                    return (
                                        <Cell
                                            key={`cell-p1-${index}`}
                                            fill={color}
                                            opacity={opacity}
                                            style={{ cursor: 'pointer' }}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 3 : 0}
                                        />
                                    );
                                })}
                            </Pie>
                            <Tooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div style={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '6px',
                                                padding: '12px 16px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                fontSize: '13px',
                                            }}>
                                                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#262626', fontSize: '14px' }}>
                                                    {data.displayName}
                                                </p>
                                                <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#262626', fontSize: '24px', lineHeight: '1.2' }}>
                                                    {data.value}
                                                </p>
                                                <p style={{ margin: 0, color: '#595959' }}>
                                                    {t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{data.percent.toFixed(2)}%</strong>
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>

                    {/* Gráfico Período 2 */}
                    <ResponsiveContainer 
                        width={individualChartWidth} 
                        height={chartHeight}
                    >
                        <PieChart>
                            <Pie
                                data={chartDataPeriod2}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                minAngle={5}
                                paddingAngle={2}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                                onClick={onPieClickPeriod2}
                                onMouseEnter={(data, index, e) => {
                                    if (data && data.payload) {
                                        const category = data.payload.categoryName;
                                        if (category !== hoveredCategory) {
                                            onListItemMouseEnter(category);
                                        }
                                    }
                                }}
                                onMouseLeave={onListItemMouseLeave}
                                cursor={disabled ? "not-allowed" : "pointer"}
                                label={shouldShowLabels ? ({ payload, cx, cy, midAngle, outerRadius, index }) => {
                                    if (!payload) return null;
                                    const { value, percent } = payload;
                                    const RADIAN = Math.PI / 180;
                                    const radius = outerRadius + 30;
                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                    const percentValue = Number(percent) || 0;
                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            fill="#262626"
                                            textAnchor={x > cx ? "start" : "end"}
                                            dominantBaseline="central"
                                            fontSize="11px"
                                            fontWeight="600"
                                        >
                                            <tspan>{value} ({percentValue.toFixed(2)}%)</tspan>
                                        </text>
                                    );
                                } : false}
                            >
                                {chartDataPeriod2.map((entry, index) => {
                                    const opacity = hoveredCategory
                                        ? (entry.categoryName === hoveredCategory ? 1 : 0.3)
                                        : (activeCategories.length === 0 || activeCategories.includes(entry.categoryName) ? 1 : 0.3);
                                    const isHovered = hoveredCategory === entry.categoryName;
                                    const isActive = activeCategories.length === 0 || activeCategories.includes(entry.categoryName);
                                    const color = getColor(entry.categoryName, entry.period);
                                    
                                    return (
                                        <Cell
                                            key={`cell-p2-${index}`}
                                            fill={color}
                                            opacity={opacity}
                                            style={{ cursor: 'pointer' }}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 3 : 0}
                                        />
                                    );
                                })}
                            </Pie>
                            <Tooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <div style={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '6px',
                                                padding: '12px 16px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                fontSize: '13px',
                                            }}>
                                                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#262626', fontSize: '14px' }}>
                                                    {data.displayName}
                                                </p>
                                                <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#262626', fontSize: '24px', lineHeight: '1.2' }}>
                                                    {data.value}
                                                </p>
                                                <p style={{ margin: 0, color: '#595959' }}>
                                                    {t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{data.percent.toFixed(2)}%</strong>
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            )}

            {shouldShowDetails && (!isFourColumns || showDetails) && (
                <div
                    style={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        width: renderMode === 'details' ? '100%' : detailsWidth,
                        paddingLeft: 8,
                        paddingRight: 0,
                        transition: 'width 0.3s ease, opacity 0.3s ease',
                        opacity: isFourColumns && !showDetails ? 0 : 1,
                        animation: isFourColumns && showDetails ? 'fadeIn 0.3s ease' : 'none'
                    }}
                >
                    <h4 style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: '#262626' }}>{t('Dashboard.Details')}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {listData.map((entry, index) => {
                        const isOthers = entry.name === "OTHERS";
                        const isOthersExpanded = isOthers && showOthersItems;
                        
                        return (
                            <Fragment key={`list-item-${index}`}>
                                <AntTooltip title={`${entry.displayName}: P1: ${entry.valuePeriod1} (${entry.percentPeriod1.toFixed(2)}%) | P2: ${entry.valuePeriod2} (${entry.percentPeriod2.toFixed(2)}%)`}>
                                    <li
                                        onMouseEnter={() => !isOthers && onListItemMouseEnter(entry.name)}
                                        onMouseLeave={onListItemMouseLeave}
                                        onClick={(e) => onListItemClick(entry.name, e)}
                                        style={{
                                            padding: '3px 4px',
                                            marginBottom: 1,
                                            cursor: 'pointer',
                                            backgroundColor: activeCategories.includes(entry.name) ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                            borderRadius: '3px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'nowrap',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: 13,
                                            transition: 'all 0.2s ease',
                                            border: activeCategories.includes(entry.name) ? '1px solid #4085c6' : '1px solid transparent',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                            {isOthers && (
                                                <span style={{ marginRight: '4px', fontSize: '10px' }}>
                                                    {isOthersExpanded ? <UpOutlined /> : <DownOutlined />}
                                                </span>
                                            )}
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    backgroundColor: categoryColors[entry.name] || '#CCCCCC',
                                                    borderRadius: '2px',
                                                    marginRight: '4px',
                                                    flexShrink: 0,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }}
                                            ></span>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.displayName}</span>
                                        </div>
                                        <strong style={{ ...legendItemStyle, marginLeft: 3, color: '#595959', flexShrink: 0, fontSize: 12 }}>
                                            <span style={legendItemStyle}>
                                                <PeriodDot period={1} size={8} />
                                                P1: {entry.valuePeriod1} ({entry.percentPeriod1.toFixed(2)}%)
                                            </span>
                                            <span style={legendSeparatorStyle}>|</span>
                                            <span style={legendItemStyle}>
                                                <PeriodDot period={2} size={8} />
                                                P2: {entry.valuePeriod2} ({entry.percentPeriod2.toFixed(2)}%)
                                            </span>
                                        </strong>
                                    </li>
                                </AntTooltip>
                                
                                {isOthers && isOthersExpanded && othersGroup.length > 0 && (
                                    <li style={{ paddingLeft: '20px', marginTop: '4px', marginBottom: '4px' }}>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                            {othersGroup.map((item, itemIndex) => {
                                                const itemPercentPeriod1 = totalPeriod1 > 0 ? (item.valuePeriod1 / totalPeriod1) * 100 : 0;
                                                const itemPercentPeriod2 = totalPeriod2 > 0 ? (item.valuePeriod2 / totalPeriod2) * 100 : 0;
                                                const isItemActive = activeCategories.includes(item.name);
                                                return (
                                                    <li
                                                        key={`others-item-${itemIndex}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSelection(item.name);
                                                        }}
                                                        onMouseEnter={() => onListItemMouseEnter(item.name)}
                                                        onMouseLeave={onListItemMouseLeave}
                                                        style={{
                                                            padding: '3px 4px',
                                                            marginBottom: 1,
                                                            cursor: 'pointer',
                                                            backgroundColor: isItemActive ? 'rgba(64, 133, 198, 0.08)' : 'transparent',
                                                            borderRadius: '3px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            fontSize: 12,
                                                            transition: 'all 0.2s ease',
                                                            border: isItemActive ? '1px solid #4085c6' : '1px solid transparent',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                            <Checkbox
                                                                checked={isItemActive}
                                                                onChange={() => toggleSelection(item.name)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ marginRight: '6px' }}
                                                            />
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.name}
                                                            </span>
                                                        </div>
                                                        <strong style={{ marginLeft: 3, color: '#595959', flexShrink: 0, fontSize: 11, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <span style={legendItemStyle}>
                                                                <PeriodDot period={1} size={8} />
                                                                P1: {item.valuePeriod1} ({itemPercentPeriod1.toFixed(2)}%)
                                                            </span>
                                                            <span style={legendSeparatorStyle}>|</span>
                                                            <span style={legendItemStyle}>
                                                                <PeriodDot period={2} size={8} />
                                                                P2: {item.valuePeriod2} ({itemPercentPeriod2.toFixed(2)}%)
                                                            </span>
                                                        </strong>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                )}
                            </Fragment>
                        );
                    })}
                </ul>
                </div>
            )}
        </div>
    );
});

DonutChartComparison.displayName = 'DonutChartComparison';

export default DonutChartComparison;
